'use client';

import a__default, { memo, forwardRef } from "react";
import { absoluteCentered } from "../../../css-utils.js";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner.js";
import { Text } from "../Text/Text.js";
import { Check } from "../../../react-icons.js";
import { CustomAnchor } from "../../quarks/CustomAnchor/CustomAnchor.js";
import { newWindowIndication, defaultDoneLabel } from "./Button.data.js";
import { getAnchorContainerClassNames } from "./utils/getAnchorContainerClassNames.js";
import { getAnchorContentClassNames } from "./utils/getAnchorContentClassNames.js";
import { getButtonContainerClassNames } from "./utils/getButtonContainerClassNames.js";
import { getButtonContentClassNames } from "./utils/getButtonContentClassNames.js";
import { processAnchorProps } from "./utils/processAnchorProps.js";
import { processButtonProps } from "./utils/processButtonProps.js";
import { successIcon } from "./Button.css.js";
import { getIconSize } from "./utils/getIconSize.js";
const Button = /* @__PURE__ */ memo(forwardRef(ButtonComponent));
function ButtonComponent(props, ref) {
  if ("href" in props && typeof props.href !== "undefined") {
    return renderAsAnchor(props, ref);
  }
  return renderAsButton(props, ref);
}
function renderAsAnchor(props, ref) {
  const p = processAnchorProps(props);
  return /* @__PURE__ */ a__default.createElement(
    CustomAnchor,
    {
      "data-qa": p.dataQa,
      ...p.propsToSpread,
      className: getAnchorContainerClassNames(p),
      href: p.href,
      ref,
      target: p.target
    },
    /* @__PURE__ */ a__default.createElement("span", { className: getAnchorContentClassNames(p) }, p.iconPosition === "lead" && p.iconJsx, /* @__PURE__ */ a__default.createElement(
      Text,
      {
        color: p.textColor,
        variant: `label.${p.size}`,
        visuallyHidden: p.hiddenLabel && !!p.Icon
      },
      p.children
    ), p.target === "_blank" && /* @__PURE__ */ a__default.createElement(Text, { visuallyHidden: true }, newWindowIndication), p.hasA11ylabel && /* @__PURE__ */ a__default.createElement(Text, { visuallyHidden: true }, p.a11yLabel), p.iconPosition !== "lead" && p.iconJsx)
  );
}
function renderAsButton(rawProps, ref) {
  const p = processButtonProps(rawProps);
  let spinnerJsx = null;
  let doneJsx = null;
  const isLoading = !!p.isLoading && !p.isSuccess;
  const isSuccess = !!p.isSuccess;
  const isInteractive = !isLoading && !isSuccess && !p.disabled;
  if (isLoading) {
    spinnerJsx = /* @__PURE__ */ a__default.createElement("span", { className: absoluteCentered }, /* @__PURE__ */ a__default.createElement(
      LoadingSpinner,
      {
        a11yLabel: p.a11yLoadingLabel,
        size: p.loadingSize,
        onDark: !p.disabled && p.isLoadingSpinnerOnDark
      }
    ));
  }
  if (isSuccess) {
    doneJsx = /* @__PURE__ */ a__default.createElement("span", { className: absoluteCentered }, /* @__PURE__ */ a__default.createElement(Text, { visuallyHidden: true }, p.a11ySuccessLabel || defaultDoneLabel), /* @__PURE__ */ a__default.createElement(
      Check,
      {
        className: successIcon,
        size: getIconSize({ size: p.size })
      }
    ));
  }
  return /* @__PURE__ */ a__default.createElement(
    "button",
    {
      "data-qa": p.dataQa,
      ...p.propsToSpread,
      "aria-disabled": p.disabled || isLoading || void 0,
      "aria-live": !isInteractive ? "polite" : void 0,
      className: getButtonContainerClassNames(p),
      ref,
      type: p.type === "submit" ? "submit" : "button",
      onClick: !isInteractive ? (e) => e.preventDefault() : p.onClick
    },
    spinnerJsx,
    doneJsx,
    /* @__PURE__ */ a__default.createElement("span", { className: getButtonContentClassNames(p) }, p.iconPosition === "lead" && p.iconJsx, /* @__PURE__ */ a__default.createElement(
      Text,
      {
        "aria-hidden": p.hasA11ylabel || isSuccess || isLoading ? "true" : void 0,
        color: p.textColor,
        variant: `label.${p.size}`,
        visuallyHidden: p.isLabelHidden
      },
      p.children
    ), p.hasA11ylabel && !isSuccess && !isLoading && /* @__PURE__ */ a__default.createElement(Text, { visuallyHidden: true }, p.a11yLabel), p.iconPosition !== "lead" && p.iconJsx)
  );
}
export {
  Button
};
